import { bool, elementType, object } from "prop-types";
import { forwardRef, useMemo, useRef } from "react";

import { styled } from "styled-components";

import { useMemberToobarContext } from "CrossFeatureComponents/MemberToolbar/MemberToolbarState";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import { colors, colorsByFunction, marketingColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import { useIsHovered } from "Utilities/Hooks/useIsHoverFocused";

const hoverGrey = addOpacity(marketingColors.grey, 0.2);

const ButtonWrapper = forwardRef(
  (
    { DisplayComponent, isActive, disabled, displayComponentProps, ...rest },
    ref
  ) => {
    const localRef = useRef();
    const buttonRef = ref || localRef;
    const isHovered = useIsHovered(buttonRef);
    const { isDark, accentColor } = useMemberToobarContext();

    const displayComponentColor = useMemo(() => {
      if (isDark) {
        if (disabled) return colorsByFunction.textSecondaryLight;
        if (isActive) {
          if (isHovered && !disabled) return accentColor;
          return accentColor;
        }
        if (isHovered && !disabled) return marketingColors.grey;
        return marketingColors.grey;
      }

      if (disabled) return colorsByFunction.textSecondaryLight;
      if (isActive) {
        if (isHovered && !disabled) return marketingColors.grey;
        return accentColor;
      }
      if (isHovered && !disabled) return marketingColors.grey;
      return accentColor;
    }, [isHovered, isDark, accentColor, isActive, disabled]);

    return (
      <SFnrButtonWrapper
        $isActive={isActive}
        $isDark={isDark}
        $accentColor={accentColor}
        disabled={disabled}
        ref={buttonRef}
        {...rest}
      >
        <DisplayComponent
          color={displayComponentColor}
          {...displayComponentProps}
        />
      </SFnrButtonWrapper>
    );
  }
);

const backgroundColor = ({ $isDark, $isActive }) => {
  if ($isDark) {
    if ($isActive) {
      return marketingColors.grey;
    }
    return colors.transparent;
  }

  if ($isActive) {
    return marketingColors.lightBlue;
  }
  return colors.transparent;
};

const hoveredBackgroundColor = ({ $isDark, $isActive, $accentColor }) => {
  if ($isDark) {
    if ($isActive) {
      return marketingColors.grey;
    }
    return hoverGrey;
  }

  if ($isActive) {
    return $accentColor;
  }
  return $accentColor;
};

const keyboardFocusedStyles = ({ $isActive, $accentColor }) => {
  if ($isActive)
    return `
      box-shadow: 0 0 0 2px ${$accentColor};
  `;
  return `
    background-color: ${hoverGrey};
  `;
};

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  background-color: ${backgroundColor};
  &:hover:not(:disabled) {
    background-color: ${hoveredBackgroundColor};
  }
  /* only show for keyboard users */
  &:focus-visible {
    ${keyboardFocusedStyles}
  }
  padding: 8px 0px;
  margin: -8px 0px;
  border-radius: 20px;
  height: 40px;
  transition: background-color 300ms ease-in-out, box-shadow 300ms ease-in-out;
`;

ButtonWrapper.propTypes = {
  DisplayComponent: elementType,
  isActive: bool,
  disabled: bool,
  displayComponentProps: object,
};

export { ButtonWrapper };
